import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FiUsers, 
  FiBookOpen, 
  FiSettings, 
  FiBarChart2, 
  FiUserPlus, 
  FiShield, 
  FiClock,
  FiTrendingUp,
  FiFileText,
  FiAlertTriangle
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';

const AdminDashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const WelcomeSection = styled.div`
  margin-bottom: 2rem;
`;

const WelcomeTitle = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
  text-transform: none !important;
  direction: rtl;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const WelcomeSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const QuickActionCard = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  transition: var(--transition);
  cursor: pointer;
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const QuickActionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const QuickActionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    switch (props.$color) {
      case 'primary': return 'linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%)';
      case 'success': return 'linear-gradient(135deg, var(--success-color) 0%, var(--success-dark) 100%)';
      case 'warning': return 'linear-gradient(135deg, var(--warning-color) 0%, var(--warning-dark) 100%)';
      case 'info': return 'linear-gradient(135deg, var(--info-color) 0%, var(--info-dark) 100%)';
      case 'danger': return 'linear-gradient(135deg, var(--error-color) 0%, var(--error-dark) 100%)';
      default: return 'linear-gradient(135deg, var(--text-muted) 0%, var(--text-secondary) 100%)';
    }
  }};
  color: var(--text-inverse);
  font-size: 1.25rem;
`;

const QuickActionContent = styled.div`
  flex: 1;
`;

const QuickActionTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  font-family: 'Inter', sans-serif;
`;

const QuickActionDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.4;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

const StatCard = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  transition: var(--transition);
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    switch (props.color) {
      case 'primary': return 'rgba(216, 184, 137, 0.1)';
      case 'success': return 'rgba(34, 197, 94, 0.1)';
      case 'warning': return 'rgba(245, 158, 11, 0.1)';
      case 'info': return 'rgba(59, 130, 246, 0.1)';
      case 'danger': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(107, 114, 128, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.color) {
      case 'primary': return 'var(--primary-color)';
      case 'success': return 'var(--success-color)';
      case 'warning': return 'var(--warning-color)';
      case 'info': return 'var(--info-color)';
      case 'danger': return 'var(--error-color)';
      default: return 'var(--text-muted)';
    }
  }};
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const FullWidthContainer = styled.div`
  background-color: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  margin-bottom: 2rem;
`;

const Section = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 1.5rem;
  
  h2 {
    color: var(--text-primary);
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: 'Inter', sans-serif;
    
    @media (max-width: 768px) {
      font-size: 1.125rem;
    }
  }
`;

const RecentActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--background-secondary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  transition: var(--transition);
  
  &:hover {
    background: var(--background-tertiary);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
  }
`;

const ActivityIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(216, 184, 137, 0.1);
  color: var(--primary-color);
  flex-shrink: 0;
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  color: var(--text-primary);
  font-weight: 500;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
`;

const ActivityTime = styled.div`
  color: var(--text-secondary);
  font-size: 0.75rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
  
  svg {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    opacity: 0.5;
  }
  
  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;

function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCourses: 0,
    totalInstructors: 0,
    totalStudents: 0,
    activeSessions: 0,
    pendingApprovals: 0
  });

  useEffect(() => {
    fetchAdminStats();
  }, []);

  const fetchAdminStats = async () => {
    try {
      // You can add API calls here to fetch admin-specific statistics
      // For now, using mock data
      setStats({
        totalUsers: 156,
        totalCourses: 12,
        totalInstructors: 8,
        totalStudents: 148,
        activeSessions: 3,
        pendingApprovals: 2
      });
    } catch (error) {
      logger.error('Error fetching admin stats', { error });
    }
  };

  const quickActions = [
    {
      title: 'User Management',
      description: 'Manage users, roles, and permissions',
      icon: <FiUsers size={24} />,
      color: 'primary',
      onClick: () => navigate('/admin/users')
    },
    {
      title: 'Course Management',
      description: 'Create and manage courses',
      icon: <FiBookOpen size={24} />,
      color: 'success',
      onClick: () => navigate('/admin/courses')
    },
    {
      title: 'Attendance Management',
      description: 'Monitor and manage attendance',
      icon: <FiClock size={24} />,
      color: 'info',
      onClick: () => navigate('/admin/attendance')
    },
    {
      title: 'System Settings',
      description: 'Configure system preferences',
      icon: <FiSettings size={24} />,
      color: 'warning',
      onClick: () => navigate('/admin/settings')
    },
    {
      title: 'Analytics & Reports',
      description: 'View system analytics and reports',
      icon: <FiBarChart2 size={24} />,
      color: 'info',
      onClick: () => navigate('/admin/analytics')
    },
    {
      title: 'Content Management',
      description: 'Manage course content and materials',
      icon: <FiFileText size={24} />,
      color: 'success',
      onClick: () => navigate('/admin/content')
    }
  ];

  const recentActivities = [
    {
      id: 1,
      title: 'New student registration: Ahmed Al-Rashid',
      time: '2 hours ago',
      type: 'user'
    },
    {
      id: 2,
      title: 'Course "Advanced Arabic Grammar" created',
      time: '4 hours ago',
      type: 'course'
    },
    {
      id: 3,
      title: 'Attendance session started for Islamic Studies',
      time: '6 hours ago',
      type: 'attendance'
    },
    {
      id: 4,
      title: 'System backup completed successfully',
      time: '1 day ago',
      type: 'system'
    }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user':
        return <FiUserPlus size={16} />;
      case 'course':
        return <FiBookOpen size={16} />;
      case 'attendance':
        return <FiClock size={16} />;
      case 'system':
        return <FiShield size={16} />;
      default:
        return <FiSettings size={16} />;
    }
  };

  return (
    <AdminDashboardContainer>
      <WelcomeSection>
        <WelcomeTitle>
          السلام عليكم {user?.first_name}
        </WelcomeTitle>
        <WelcomeSubtitle>Welcome to the admin dashboard. Manage your system efficiently.</WelcomeSubtitle>
      </WelcomeSection>

      {/* Quick Actions */}
      <QuickActionsGrid>
        {quickActions.map((action, index) => (
          <QuickActionCard
            key={action.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            onClick={action.onClick}
          >
            <QuickActionHeader>
              <QuickActionIcon $color={action.color}>
                {action.icon}
              </QuickActionIcon>
              <QuickActionContent>
                <QuickActionTitle>{action.title}</QuickActionTitle>
                <QuickActionDescription>{action.description}</QuickActionDescription>
              </QuickActionContent>
            </QuickActionHeader>
          </QuickActionCard>
        ))}
      </QuickActionsGrid>

      {/* Statistics */}
      <StatsGrid>
        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <StatHeader>
            <StatIcon color="primary">
              <FiUsers size={20} />
            </StatIcon>
            <StatContent>
              <StatValue>{stats.totalUsers}</StatValue>
              <StatLabel>Total Users</StatLabel>
            </StatContent>
          </StatHeader>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <StatHeader>
            <StatIcon color="success">
              <FiBookOpen size={20} />
            </StatIcon>
            <StatContent>
              <StatValue>{stats.totalCourses}</StatValue>
              <StatLabel>Total Courses</StatLabel>
            </StatContent>
          </StatHeader>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <StatHeader>
            <StatIcon color="info">
              <FiShield size={20} />
            </StatIcon>
            <StatContent>
              <StatValue>{stats.totalInstructors}</StatValue>
              <StatLabel>Instructors</StatLabel>
            </StatContent>
          </StatHeader>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <StatHeader>
            <StatIcon color="warning">
              <FiTrendingUp size={20} />
            </StatIcon>
            <StatContent>
              <StatValue>{stats.totalStudents}</StatValue>
              <StatLabel>Students</StatLabel>
            </StatContent>
          </StatHeader>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <StatHeader>
            <StatIcon color="info">
              <FiClock size={20} />
            </StatIcon>
            <StatContent>
              <StatValue>{stats.activeSessions}</StatValue>
              <StatLabel>Active Sessions</StatLabel>
            </StatContent>
          </StatHeader>
        </StatCard>

        <StatCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          <StatHeader>
            <StatIcon color="danger">
              <FiAlertTriangle size={20} />
            </StatIcon>
            <StatContent>
              <StatValue>{stats.pendingApprovals}</StatValue>
              <StatLabel>Pending Approvals</StatLabel>
            </StatContent>
          </StatHeader>
        </StatCard>
      </StatsGrid>

      {/* Recent Activity */}
      <FullWidthContainer>
        <Section>
          <SectionHeader>
            <h2>
              <FiClock size={20} />
              Recent Activity
            </h2>
          </SectionHeader>
          <RecentActivityList>
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => (
                <ActivityItem key={activity.id}>
                  <ActivityIcon>
                    {getActivityIcon(activity.type)}
                  </ActivityIcon>
                  <ActivityContent>
                    <ActivityTitle>{activity.title}</ActivityTitle>
                    <ActivityTime>{activity.time}</ActivityTime>
                  </ActivityContent>
                </ActivityItem>
              ))
            ) : (
              <EmptyState>
                <FiClock />
                <p>No recent activity.</p>
              </EmptyState>
            )}
          </RecentActivityList>
        </Section>
      </FullWidthContainer>
    </AdminDashboardContainer>
  );
}

export default AdminDashboard;
